import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Download, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import client, { API } from "@/lib/api";
import { CONFIG } from "./adminConfig";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CountrySelect, NationalitySelect, PhoneInput, OptionSelect, splitPhone, isValidPhone, todayISO, toInputDate } from "@/components/FormControls";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function fmt(v, type) {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "date") { const d = new Date(v); if (isNaN(d)) return v; return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

export default function ResourcePage({ resource: forced }) {
  const params = useParams();
  const resource = forced || params.resource;
  const cfg = CONFIG[resource] || { title: resource, columns: [] };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | {} (new) | item (edit)
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/records/${resource}`, {
        params: { page, limit, search, ...filters },
      });
      let cols = cfg.columns;
      if ((!cols || cols.length === 0) && data.items[0]) {
        cols = Object.keys(data.items[0]).filter((k) => !["id", "password_hash"].includes(k)).slice(0, 6).map((k) => [k, k]);
        cfg.columns = cols;
      }
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [resource, page, search, filters]); // eslint-disable-line

  useEffect(() => { setPage(1); setSearch(""); setFilters({}); }, [resource]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const save = async (form) => {
    try {
      if (dialog && dialog.id) await client.put(`/records/${resource}/${dialog.id}`, form);
      else await client.post(`/records/${resource}`, form);
      toast.success("Saved");
      setDialog(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail || "Save failed";
      if (String(d).startsWith("Possible duplicate")) {
        if (window.confirm(`${d}. Save anyway?`)) return save({ ...form, force: true });
      } else toast.error(d);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await client.delete(`/records/${resource}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const columns = cfg.columns || [];
  const pages = Math.ceil(total / limit) || 1;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0A192F]">{cfg.title}</h1>
          <p className="text-sm text-slate-400">{total} records</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 px-3 bg-white rounded-lg border border-slate-200">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="resource-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..." className="py-2 outline-none text-sm bg-transparent w-40" />
          </div>
          {(cfg.filters || []).map((f) => (
            <select key={f.name} data-testid={`filter-${f.name}`} value={filters[f.name] || "all"}
              onChange={(e) => setFilters({ ...filters, [f.name]: e.target.value })}
              className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm">
              <option value="all">All {f.label}</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <a href={`${API}/export/${resource}`} data-testid="resource-export">
            <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Export</Button>
          </a>
          {!cfg.readOnly && (
            <Button data-testid="resource-add" size="sm" onClick={() => setDialog({})} className="bg-[#0088FF] hover:bg-[#0070E0]">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                {columns.map((c) => <th key={c[0]} className="text-left px-4 py-3 whitespace-nowrap">{c[1]}</th>)}
                {!cfg.readOnly && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">No records found.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c[0]} className="px-4 py-3 whitespace-nowrap">
                      {c[2] === "status" ? <StatusBadge value={it[c[0]]} /> :
                        c[2] === "stage" ? <StatusBadge value={it[c[0]]} kind="stage" /> :
                        fmt(it[c[0]], c[2])}
                    </td>
                  ))}
                  {!cfg.readOnly && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-${it.id}`} onClick={() => setDialog(it)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-${it.id}`} onClick={() => del(it.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {dialog !== null && <RecordDialog cfg={cfg} item={dialog} onClose={() => setDialog(null)} onSave={save} />}
    </div>
  );
}

function RecordDialog({ cfg, item, onClose, onSave }) {
  const fields = cfg.fields || (cfg.columns || []).map((c) => ({ name: c[0], label: c[1], type: "text" }));
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach((fl) => {
      if (fl.type === "phone") f[fl.name] = splitPhone(item[fl.name], item[`${fl.name}_code`]);
      else f[fl.name] = item[fl.name] ?? "";
    });
    return f;
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setV = (k) => (v) => setForm({ ...form, [k]: v });
  const submit = () => {
    const out = { ...form };
    for (const fl of fields) {
      if (fl.type !== "phone") continue;
      if (!isValidPhone(out[fl.name].number)) return toast.error(`${fl.label}: enter 6 to 14 digits`);
      out[`${fl.name}_code`] = out[fl.name].code;
      out[fl.name] = out[fl.name].number;
    }
    onSave(out);
  };
  const control = (fl) => {
    const tid = `field-${fl.name}`;
    if (fl.type === "country") return <CountrySelect testId={tid} value={form[fl.name]} onChange={setV(fl.name)} />;
    if (fl.type === "nationality") return <NationalitySelect testId={tid} value={form[fl.name]} onChange={setV(fl.name)} />;
    if (fl.type === "phone") return <PhoneInput testId={tid} value={form[fl.name]} onChange={setV(fl.name)} />;
    if (fl.type === "select") return <OptionSelect testId={tid} value={form[fl.name]} onChange={setV(fl.name)} options={fl.opts || []} />;
    if (fl.type === "textarea") return <Textarea data-testid={tid} value={form[fl.name]} onChange={set(fl.name)} rows={3} />;
    return (
      <Input data-testid={tid} type={["number", "date", "datetime-local", "password"].includes(fl.type) ? fl.type : "text"}
        max={fl.max === "today" ? todayISO() : undefined}
        value={fl.type === "date" && form[fl.name] ? (toInputDate(form[fl.name]) || String(form[fl.name]).slice(0, 10)) : form[fl.name]} onChange={set(fl.name)} />
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item.id ? "Edit" : "New"} {cfg.title}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((fl) => (
            <div key={fl.name} className={fl.type === "textarea" ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{fl.label}</Label>
              {control(fl)}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="record-save" onClick={submit} className="bg-[#0088FF] hover:bg-[#0070E0]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
